import React, { createContext, useContext, useReducer } from 'react';

const CVContext = createContext();

// Load state from localStorage
const loadStateFromStorage = () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const savedProfile = localStorage.getItem('cvProfile');
    const savedSession = localStorage.getItem('cvSession');
    const savedEntryType = localStorage.getItem('cvEntryType');
    const savedInteractionMode = localStorage.getItem('cvInteractionMode');
    return {
      profile: savedProfile ? JSON.parse(savedProfile) : null,
      session: savedSession ? JSON.parse(savedSession) : null,
      entryType: savedEntryType || null,
      interactionMode: savedInteractionMode || null,
    };
  } catch (error) {
    console.error('Error loading CV state from localStorage:', error);
    return { profile: null, session: null, entryType: null, interactionMode: null };
  }
};

// Initial state factory function for lazy initialization
const createInitialState = () => {
  const stored = loadStateFromStorage();
  return {
    profile: stored?.profile || null,
    session: stored?.session || null,
    messages: [],
    currentSection: 'greeting',
    isLoading: false,
    error: null,
    entryType: stored?.entryType || null,
    interactionMode: stored?.interactionMode || null,
  };
};

function cvReducer(state, action) {
  let newState;
  
  switch (action.type) {
    case 'SET_PROFILE':
      // Create a completely new object reference to trigger re-renders
      newState = { ...state, profile: action.payload ? { ...action.payload } : null };
      if (action.payload) {
        try {
          localStorage.setItem('cvProfile', JSON.stringify(action.payload));
          console.log('CVContext: Profile saved to localStorage');
        } catch (e) {
          console.error('Error saving profile to localStorage:', e);
        }
      }
      return newState;
      
    case 'SET_SESSION':
      newState = { ...state, session: action.payload, messages: action.payload?.messages || [] };
      if (action.payload) {
        try {
          localStorage.setItem('cvSession', JSON.stringify(action.payload));
        } catch (e) {
          console.error('Error saving session to localStorage:', e);
        }
      } else {
        // Clear session from localStorage when setting to null
        try {
          localStorage.removeItem('cvSession');
          console.log('CVContext: Session cleared from localStorage');
        } catch (e) {
          console.error('Error clearing session from localStorage:', e);
        }
      }
      return newState;
      
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
      
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
      
    case 'SET_CURRENT_SECTION':
      return { ...state, currentSection: action.payload };
      
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
      
    case 'SET_ERROR':
      return { ...state, error: action.payload };
      
    case 'SET_ENTRY_TYPE':
      try {
        if (action.payload) {
          localStorage.setItem('cvEntryType', action.payload);
        } else {
          localStorage.removeItem('cvEntryType');
        }
      } catch (e) {
        console.error('Error saving entryType to localStorage:', e);
      }
      return { ...state, entryType: action.payload };
      
    case 'SET_INTERACTION_MODE':
      try {
        if (action.payload) {
          localStorage.setItem('cvInteractionMode', action.payload);
        } else {
          localStorage.removeItem('cvInteractionMode');
        }
      } catch (e) {
        console.error('Error saving interactionMode to localStorage:', e);
      }
      return { ...state, interactionMode: action.payload };
      
    case 'UPDATE_PROFILE':
      const updatedProfile = { ...state.profile, ...action.payload };
      try {
        localStorage.setItem('cvProfile', JSON.stringify(updatedProfile));
      } catch (e) {
        console.error('Error updating profile in localStorage:', e);
      }
      return { ...state, profile: updatedProfile };
      
    case 'RESET':
      try {
        localStorage.removeItem('cvProfile');
        localStorage.removeItem('cvSession');
        localStorage.removeItem('cvEntryType');
        localStorage.removeItem('cvInteractionMode');
      } catch (e) {
        console.error('Error clearing localStorage:', e);
      }
      return createInitialState();
      
    default:
      return state;
  }
}

export function CVProvider({ children }) {
  // Use lazy initialization to ensure localStorage is available
  const [state, dispatch] = useReducer(cvReducer, null, createInitialState);

  return (
    <CVContext.Provider value={{ state, dispatch }}>
      {children}
    </CVContext.Provider>
  );
}

export function useCVContext() {
  const context = useContext(CVContext);
  if (!context) {
    throw new Error('useCVContext must be used within a CVProvider');
  }
  return context;
}
